// frontend/src/pages/manufacturing/v2/components/OrderV2ProductionSection.jsx
//
// ONE shared component used by BOTH order detail pages:
//   - CustomerOrderDetail.js  -> variant="full"    (expandable per-line WO list)
//   - DispatchOrderDetail.js   -> variant="compact" (dense one-row-per-line)
//
// Data sources (contracts #2 and #3):
//   - GET /api/v2/customer-orders/{id}/summary/  (v2.customerOrders.summary)
//       per-line: line_id, product_code, product_name, quantity_ordered,
//       allocated, shipped, outstanding, tree_id, wo_counts{total, planned,
//       in_progress, completed, cancelled}, produced_qty, scrapped_qty,
//       progress_pct
//   - GET /api/v2/work-orders/?customer_order={id}  (v2.workOrders.list)
//       fetched lazily, only when a line's WO list is expanded ('full' only).
//
// Design rules:
//   - This section must NEVER break the host V1 page. Errors fail quietly with a
//     small retry link; nothing throws upward.
//   - Empty state (no line has a tree): render a single subtle line, not a card.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, LinearProgress, Divider,
  IconButton, Collapse, Link, List, ListItem, ListItemText, Table,
  TableBody, TableCell, TableHead, TableRow, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert,
} from '@mui/material';
import { ExpandMore, ExpandLess, Factory, Science, Autorenew } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { v2 } from '../../../../services/api';
import GenerationModeBadge from './GenerationModeBadge';

// A completed/cancelled order has production closed — hide all per-line actions.
function isProductionClosed(status) {
  return status === 'complete' || status === 'cancelled';
}

// Pull a backend error message out verbatim, falling back to a generic string.
function errMsg(error, fallback) {
  return error?.response?.data?.error || fallback;
}

const WO_STATUS_COLOR = {
  planned: 'info',
  released: 'info',
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'error',
};

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const num = (v) => Math.round(v || 0).toLocaleString();

/** Small "n label" chips summarising a line's WO counts. Cancelled shown only if > 0. */
function WoCountChips({ counts }) {
  if (!counts) return null;
  const parts = [];
  if (counts.in_progress > 0) parts.push({ key: 'ip', label: `${counts.in_progress} in progress`, color: 'primary' });
  if (counts.completed > 0) parts.push({ key: 'done', label: `${counts.completed} completed`, color: 'success' });
  if (counts.planned > 0) parts.push({ key: 'planned', label: `${counts.planned} planned`, color: 'info' });
  if (counts.cancelled > 0) parts.push({ key: 'cancelled', label: `${counts.cancelled} cancelled`, color: 'error' });
  if (parts.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {parts.map((p) => (
        <Chip key={p.key} size="small" label={p.label} color={p.color} variant="outlined" />
      ))}
    </Box>
  );
}

/** Progress bar + produced/ordered + pct text. */
function LineProgress({ line }) {
  const pct = Math.max(0, Math.min(100, Math.round(line.progress_pct || 0)));
  return (
    <Box sx={{ minWidth: 140 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={pct >= 100 ? 'success' : 'primary'}
        sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
      />
      <Typography variant="caption" color="text.secondary">
        {num(line.produced_qty)} / {num(line.quantity_ordered)} produced · {pct}%
        {line.scrapped_qty > 0 && (
          <Box component="span" sx={{ color: 'error.main' }}> · Scrap {num(line.scrapped_qty)}</Box>
        )}
      </Typography>
    </Box>
  );
}

/** Expandable per-line WO list ('full' variant only). Lazily fetches the order's
 *  WOs once, then filters to this line's tree_id. */
function LineWorkOrders({ orderId, treeId, lineId }) {
  const [open, setOpen] = useState(false);
  const [wos, setWos] = useState(null); // null = not fetched yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Guard the once-only lazy fetch with a ref so a state update doesn't re-run
  // and self-cancel the request (see v2 useEffect self-cancellation gotcha).
  const fetchedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await v2.workOrders.list({ customer_order: orderId });
      const data = res.data || {};
      const rows = Array.isArray(data) ? data : (data.results || []);
      setWos(rows);
      fetchedRef.current = true;
    } catch (err) {
      console.error('Failed to load V2 work orders for order', orderId, err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !fetchedRef.current) load();
  };

  // WOs belonging to this line's tree. tree_id may be on the WO directly.
  const lineWos = useMemo(() => {
    if (!wos) return [];
    return wos.filter((wo) => (
      (treeId != null && wo.tree_id === treeId)
      || (lineId != null && (wo.order_line === lineId || wo.order_line_id === lineId))
    ));
  }, [wos, treeId, lineId]);

  return (
    <Box>
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={handleToggle}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: '0.8rem' }}
        data-testid={`line-wos-toggle-${lineId}`}
      >
        {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        {open ? 'Hide work orders' : 'Show work orders'}
      </Link>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          {loading && <LinearProgress data-testid={`line-wos-loading-${lineId}`} />}
          {error && !loading && (
            <Typography variant="caption" color="error">
              Couldn&apos;t load work orders.{' '}
              <Link component="button" type="button" onClick={load} data-testid={`line-wos-retry-${lineId}`}>
                Retry
              </Link>
            </Typography>
          )}
          {!loading && !error && wos && lineWos.length === 0 && (
            <Typography variant="caption" color="text.secondary">No work orders for this line.</Typography>
          )}
          {!loading && !error && lineWos.length > 0 && (
            <Table size="small" data-testid={`line-wos-table-${lineId}`}>
              <TableHead>
                <TableRow>
                  <TableCell>WO #</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Due</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineWos.map((wo) => (
                  <TableRow key={wo.id} data-testid={`line-wo-row-${wo.id}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{wo.wo_number}</TableCell>
                    <TableCell>
                      <Chip size="small" label={wo.status} color={WO_STATUS_COLOR[wo.status] || 'default'} />
                    </TableCell>
                    <TableCell align="right">{num(wo.quantity_to_produce)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(wo.due_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

/** "Send line to V2" button for a line that has no tree yet. Explodes just this
 *  line, then asks the parent to refresh. Surfaces 400/409 backend errors verbatim. */
function SendLineToV2Button({ lineId, onDone }) {
  const [busy, setBusy] = useState(false);

  const handleSend = async () => {
    setBusy(true);
    try {
      await v2.explode.orderLine(lineId);
      toast.success('Line sent to V2 — work orders created');
      if (onDone) onDone();
    } catch (error) {
      // A line whose ordered qty is already fully covered by allocated stock
      // comes back as a 400 — that is a GOOD outcome (nothing needs making),
      // so it gets an info toast rather than a red "failed" one.
      const data = error?.response?.data || {};
      const isCovered = data.code === 'nothing_to_produce'
        || data.covered === true
        || (error?.response?.status === 400
          && String(data.error || '').toLowerCase().includes('nothing left to produce'));
      if (isCovered) {
        toast.info('Nothing to produce — this line is fully covered by allocated stock. No work order needed.');
      } else {
        console.error('Failed to send line to V2', lineId, error);
        toast.error(errMsg(error, 'Failed to send line to V2'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="small"
      variant="outlined"
      color="secondary"
      startIcon={<Science fontSize="small" />}
      onClick={handleSend}
      disabled={busy}
      data-testid={`ov2-send-line-${lineId}`}
    >
      {busy ? 'Sending…' : 'Send line to V2'}
    </Button>
  );
}

/** "Remake…" button + dialog for a line that already has a tree. Creates
 *  additional work orders (e.g. for damaged goods). Errors show verbatim in the
 *  dialog and it stays open so the user can correct their input. */
function RemakeLineAction({ lineId, productCode, onDone }) {
  const [open, setOpen] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const openDialog = () => {
    setQuantity('');
    setNotes('');
    setError('');
    setOpen(true);
  };
  const closeDialog = () => { if (!busy) setOpen(false); };

  const qtyNum = Number(quantity);
  const qtyValid = quantity !== '' && Number.isFinite(qtyNum) && qtyNum > 0;

  const handleSubmit = async () => {
    if (!qtyValid) {
      setError('Enter a quantity greater than 0.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await v2.orderLines.remake(lineId, { quantity: qtyNum, notes });
      const wos = res.data?.work_orders || [];
      const numbers = wos.map((w) => w.wo_number).filter(Boolean);
      const summary = numbers.length
        ? `Remake created ${numbers.length} work order${numbers.length === 1 ? '' : 's'}: ${numbers.join(', ')}`
        : 'Remake work orders created';
      toast.success(summary, { autoClose: 6000 });
      setOpen(false);
      if (onDone) onDone();
    } catch (err) {
      console.error('Remake failed for line', lineId, err);
      setError(errMsg(err, 'Failed to create remake work orders'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<Autorenew fontSize="small" />}
        onClick={openDialog}
        data-testid={`ov2-remake-open-${lineId}`}
      >
        Remake…
      </Button>
      <Dialog open={open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Remake line {productCode ? `— ${productCode}` : ''}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }} data-testid={`ov2-remake-warning-${lineId}`}>
            Creates additional work orders for this line (e.g. damaged goods).
            Extra production beyond the order&apos;s outstanding stays as free stock.
          </Alert>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid={`ov2-remake-error-${lineId}`}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            required
            type="number"
            label="Quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            sx={{ mb: 2 }}
            data-testid={`ov2-remake-qty-${lineId}`}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason / notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid={`ov2-remake-notes-${lineId}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={busy || !qtyValid}
            data-testid={`ov2-remake-submit-${lineId}`}
          >
            {busy ? 'Creating…' : 'Create Remake'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Header row with the "V2 Production" title + badge. */
function SectionHeader() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Factory fontSize="small" color="primary" />
      <Typography variant="h6">V2 Production</Typography>
      <GenerationModeBadge />
    </Box>
  );
}

export default function OrderV2ProductionSection({
  orderId,
  variant = 'full',
  orderStatus,
  onChanged,
}) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Per-line send/remake actions only exist on the 'full' variant of an order
  // whose production is still open. When orderStatus isn't supplied (existing
  // callers) actions stay off, preserving prior behaviour.
  const showActions = variant === 'full' && orderStatus != null && !isProductionClosed(orderStatus);

  const load = useCallback(async () => {
    if (orderId == null) return;
    setLoading(true);
    setError(false);
    try {
      const res = await v2.customerOrders.summary(orderId);
      const data = res.data || {};
      // Contract #2 returns per-line rows; accept a bare array or {lines:[...]}.
      const rows = Array.isArray(data) ? data : (data.lines || data.results || []);
      setLines(rows);
    } catch (err) {
      console.error('Failed to load V2 production summary for order', orderId, err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  // After a per-line action succeeds, reload our own summary AND let the parent
  // refresh (order header, V1 work orders, etc.).
  const handleLineChanged = useCallback(() => {
    load();
    if (onChanged) onChanged();
  }, [load, onChanged]);

  const anyTree = useMemo(() => lines.some((l) => l.tree_id != null), [lines]);
  const anyNoTree = useMemo(() => lines.some((l) => l.tree_id == null), [lines]);

  // Error: fail quietly with a small retry link. Never break the host page.
  if (error) {
    return (
      <Box sx={{ mt: 2 }} data-testid="ov2-error">
        <Typography variant="caption" color="text.secondary">
          V2 production data unavailable.{' '}
          <Link component="button" type="button" onClick={load} data-testid="ov2-retry">Retry</Link>
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ mt: 2 }} data-testid="ov2-loading">
        <LinearProgress />
      </Box>
    );
  }

  // Empty state: no line has a tree — subtle one-liner, not a big card.
  // Exception: when per-line actions are enabled and there are lines that could
  // be sent to V2, fall through so the "Send line to V2" buttons render.
  if (!anyTree && !(showActions && anyNoTree)) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}
        data-testid="ov2-empty"
      >
        No V2 production for this order.
      </Typography>
    );
  }

  const treeLines = lines.filter((l) => l.tree_id != null);

  // ---- Compact variant (dispatch): dense one row per line ----
  if (variant === 'compact') {
    return (
      <Box sx={{ mt: 2 }} data-testid="ov2-compact">
        <SectionHeader />
        <Divider sx={{ mb: 1 }} />
        {treeLines.map((line) => (
          <Box
            key={line.line_id}
            data-testid={`ov2-compact-line-${line.line_id}`}
            sx={{
              display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
              py: 0.75, borderBottom: '1px solid', borderColor: 'divider',
            }}
          >
            <Box sx={{ minWidth: 160, flex: '1 1 160px' }}>
              <Typography variant="body2" fontWeight="bold">{line.product_code}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{line.product_name}</Typography>
            </Box>
            <Typography variant="caption" sx={{ minWidth: 200 }}>
              Ord {num(line.quantity_ordered)} · Alloc {num(line.allocated)} · Ship {num(line.shipped)} · Out {num(line.outstanding)}
            </Typography>
            <Box sx={{ flex: '1 1 160px' }}>
              <LineProgress line={line} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  // ---- Full variant (customer order): expandable per-line WO list ----
  // With actions enabled we render every line (so no-tree lines can be sent to
  // V2); otherwise only lines that already have a tree.
  const fullLines = showActions ? lines : treeLines;
  return (
    <Card sx={{ mt: 3 }} data-testid="ov2-full">
      <CardContent>
        <SectionHeader />
        <Divider sx={{ mb: 2 }} />
        <List disablePadding>
          {fullLines.map((line) => {
            const hasTree = line.tree_id != null;
            return (
              <ListItem
                key={line.line_id}
                data-testid={`ov2-full-line-${line.line_id}`}
                disableGutters
                sx={{
                  display: 'block', border: '1px solid', borderColor: 'divider',
                  borderRadius: 1, mb: 1.5, p: 1.5,
                }}
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" component="span">
                          {line.product_code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                          {line.product_name}
                        </Typography>
                      </Box>
                      <WoCountChips counts={line.wo_counts} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Ordered {num(line.quantity_ordered)} · Allocated {num(line.allocated)} ·
                        Shipped {num(line.shipped)} · Outstanding {num(line.outstanding)}
                      </Typography>
                      <LineProgress line={line} />
                      {hasTree && (
                        <Box sx={{ mt: 1 }}>
                          <LineWorkOrders orderId={orderId} treeId={line.tree_id} lineId={line.line_id} />
                        </Box>
                      )}
                      {showActions && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {hasTree ? (
                            <RemakeLineAction
                              lineId={line.line_id}
                              productCode={line.product_code}
                              onDone={handleLineChanged}
                            />
                          ) : (
                            <SendLineToV2Button lineId={line.line_id} onDone={handleLineChanged} />
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
}
